import type {
  ApiMockItem,
  TokenMockItem,
  StaticMockItem,
  ChannelMockItem,
  CreateApiMock,
  CreateChannelMock,
  CreatePlaylistMock,
  CreateVideoMock,
  CreateSiteMock,
  CreateTokenResponseMock,
  // CreateTokenMock,
  CreateStaticMock,
  PlaylistMockItem,
  VideoMockItem,
  SiteMockItem,
  TokenResponseMockItem,
} from '@preview/types';

// Utility functions
export const generateMockId = (prefix = 'mock'): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Generic helper for any API mock with explicit typing
export const createApiMock = <T>(mock: CreateApiMock<T>): ApiMockItem<T> => {
  return {
    ...mock,
    type: 'api',
    method: mock.method || 'GET',
    enabled: mock.enabled ?? true,
    createdAt: new Date().toISOString(),
    description: mock.description || '',
  };
};

// Specific helpers for known types
export const createPlaylistMock = (mock: CreatePlaylistMock): PlaylistMockItem => {
  const id = mock.id || generateMockId('playlist');
  const endpoint = mock.endpoint || `*/api/playlists/${id}*`;
  return createApiMock({ ...mock, id, endpoint });
};

export const createVideoMock = (mock: CreateVideoMock): VideoMockItem => {
  const id = mock.id || generateMockId('video');
  const endpoint = mock.endpoint || `*/ondemand/videos/${id}`;
  return createApiMock({ ...mock, id, endpoint });
};

export const createChannelMock = (mock: CreateChannelMock): ChannelMockItem => {
  const id = mock.id || generateMockId('channel');
  const endpoint = mock.endpoint || `*/api/v1/channels?url=*`;
  return createApiMock({ ...mock, id, endpoint });
};

export const createSiteMock = (mock: CreateSiteMock): SiteMockItem => {
  const id = mock.id || generateMockId('site');
  const endpoint = mock.endpoint || `*/api/v1/sites?url=*`;
  return createApiMock({ ...mock, id, endpoint });
};

export const createTokenResponseMock = (mock: CreateTokenResponseMock): TokenResponseMockItem => {
  const id = mock.id || generateMockId('token');
  const endpoint = mock.endpoint || `*/api/v1/token?url=*`;
  return createApiMock({ ...mock, id, endpoint });
};

// export const createTokenMock = (mock: CreateTokenMock): TokenMockItem => ({
//   ...mock,
//   type: 'token',
//   enabled: mock.enabled ?? true,
//   createdAt: new Date().toISOString(),
// });

export const createStaticMock = (mock: CreateStaticMock): StaticMockItem => ({
  ...mock,
  type: 'static',
  enabled: mock.enabled ?? true,
  createdAt: new Date().toISOString(),
});

export const isTokenExpired = (tokenMock: TokenMockItem): boolean => {
  if (!tokenMock.data.expiresAt) return false;
  return new Date() > new Date(tokenMock.data.expiresAt);
};
